import React, { useState, useEffect, useCallback } from 'react'
import { weeztixAPI } from '../../api/weeztix'
import {
  isAuthenticated,
  isError,
  type WeeztixHealthState,
  type WeeztixHealthResponse,
} from '../../interfaces/weeztix'

/**
 * WeeztixAuth Component
 *
 * Manages Weeztix OAuth authentication with:
 * - Health status checking
 * - OAuth login flow initiation
 * - Callback success detection
 * - Token expiration display
 */
const WeeztixAuth: React.FC = () => {
  // Component state
  const [healthState, setHealthState] = useState<WeeztixHealthState>({
    status: 'error',
    authenticated: false,
    message: 'Checking authentication status...',
  })
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [showSuccess, setShowSuccess] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * Parse health response and update component state
   */
  const parseHealthResponse = useCallback(
    (response: WeeztixHealthResponse): void => {
      if (isAuthenticated(response)) {
        setHealthState({
          status: response.status,
          authenticated: true,
          accessTokenExpiresIn: response.accessTokenExpiresIn,
          refreshTokenExpiresIn: response.refreshTokenExpiresIn,
          warning: response.warning,
        })
        setError(null)
      } else if (isError(response)) {
        setHealthState({
          status: 'error',
          authenticated: false,
          message: response.message,
        })
        setError(null)
      }
    },
    []
  )

  /**
   * Check authentication health status
   */
  const checkHealth = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await weeztixAPI.checkHealth()
      parseHealthResponse(response)
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to check health status'
      setError(errorMessage)
      setHealthState({
        status: 'error',
        authenticated: false,
        message: errorMessage,
      })
    } finally {
      setIsLoading(false)
    }
  }, [parseHealthResponse])

  /**
   * Initiate OAuth login flow
   */
  const handleLogin = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      await weeztixAPI.initiateLogin()
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to initiate login'
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Check for OAuth callback success on component mount
   */
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const hasCode = urlParams.has('code')

    if (hasCode) {
      // Show success message
      setShowSuccess(true)

      // Clean URL without reloading page
      const cleanUrl = window.location.pathname
      window.history.replaceState({}, document.title, cleanUrl)

      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setShowSuccess(false)
      }, 5000)
    }

    // Initial health check
    checkHealth()
  }, [checkHealth])

  /**
   * Get status badge color classes
   */
  const getStatusColor = (): string => {
    switch (healthState.status) {
      case 'healthy':
        return 'bg-green-100 text-green-800 border-green-300'
      case 'warning':
        return 'bg-amber-100 text-amber-800 border-amber-300'
      case 'error':
        return 'bg-red-100 text-red-800 border-red-300'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300'
    }
  }

  /**
   * Get status icon
   */
  const getStatusIcon = (): string => {
    switch (healthState.status) {
      case 'healthy':
        return '✓'
      case 'warning':
        return '⚠'
      case 'error':
        return '✕'
      default:
        return '•'
    }
  }

  /**
   * Check if login button should be disabled
   */
  const isLoginDisabled = (): boolean => {
    return healthState.status === 'healthy' && !healthState.warning
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Success Message */}
        {showSuccess && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 shadow-sm animate-fade-in">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-green-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">
                  Authentication successful!
                </h3>
                <p className="mt-1 text-sm text-green-700">
                  Your Weeztix OAuth tokens have been saved successfully.
                </p>
              </div>
              <button
                onClick={() => setShowSuccess(false)}
                className="ml-auto flex-shrink-0 text-green-500 hover:text-green-700 transition-colors"
              >
                <span className="sr-only">Dismiss</span>
                <svg
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Main Card */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <h2 className="text-2xl font-bold text-gray-900">
              Weeztix OAuth Authentication
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Manage your Weeztix API authentication and monitor token health
            </p>
          </div>

          {/* Content */}
          <div className="px-6 py-6 space-y-6">
            {/* Section 1: Status Display */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Authentication Status
              </h3>

              {/* Status Badge */}
              <div className="flex items-center space-x-3">
                <span
                  className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium border ${getStatusColor()}`}
                >
                  <span className="mr-2 text-lg">{getStatusIcon()}</span>
                  {healthState.status.charAt(0).toUpperCase() +
                    healthState.status.slice(1)}
                </span>
                <span className="text-sm text-gray-600">
                  {healthState.authenticated
                    ? 'Authenticated'
                    : 'Not Authenticated'}
                </span>
              </div>

              {/* Token Information (when authenticated) */}
              {healthState.authenticated && (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <svg
                        className="h-5 w-5 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                      </svg>
                      <span className="text-sm font-medium text-gray-700">
                        Access Token
                      </span>
                    </div>
                    <span className="text-sm font-mono text-gray-900">
                      Expires in {healthState.accessTokenExpiresIn}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <svg
                        className="h-5 w-5 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                      <span className="text-sm font-medium text-gray-700">
                        Refresh Token
                      </span>
                    </div>
                    <span className="text-sm font-mono text-gray-900">
                      Expires in {healthState.refreshTokenExpiresIn}
                    </span>
                  </div>
                </div>
              )}

              {/* Warning Message */}
              {healthState.warning && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <svg
                      className="h-5 w-5 text-amber-400 mt-0.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <p className="ml-3 text-sm text-amber-800">
                      {healthState.warning}
                    </p>
                  </div>
                </div>
              )}

              {/* Error/Info Message */}
              {healthState.message && !healthState.authenticated && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800">{healthState.message}</p>
                </div>
              )}

              {/* Error Alert */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <svg
                      className="h-5 w-5 text-red-400 mt-0.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <p className="ml-3 text-sm text-red-800">{error}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Section 2: Action Buttons */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Actions</h3>
              <div className="flex flex-col sm:flex-row gap-3">
                {/* Check Health Button */}
                <button
                  onClick={checkHealth}
                  disabled={isLoading}
                  className="flex-1 inline-flex items-center justify-center px-6 py-3 border border-gray-300 shadow-sm text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-700"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Checking...
                    </>
                  ) : (
                    <>
                      <svg
                        className="-ml-1 mr-3 h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      Check Health
                    </>
                  )}
                </button>

                {/* Login Button */}
                <button
                  onClick={handleLogin}
                  disabled={isLoginDisabled() || isLoading}
                  className={`flex-1 inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 ${
                    isLoginDisabled() || isLoading
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transform hover:scale-[1.02]'
                  }`}
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Opening...
                    </>
                  ) : (
                    <>
                      <svg
                        className="-ml-1 mr-3 h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                        />
                      </svg>
                      Login to Weeztix
                    </>
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500">
                {isLoginDisabled()
                  ? 'Login is disabled when authentication is healthy'
                  : 'Click to authenticate with Weeztix OAuth (opens in new window)'}
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <p className="text-xs text-gray-600">
              <span className="font-medium">Note:</span> Tokens are
              automatically managed by the backend. Check health regularly to
              monitor expiration status.
            </p>
          </div>
        </div>

        {/* Additional Info Card */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg
              className="h-5 w-5 text-blue-400 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                How it works
              </h3>
              <div className="mt-2 text-sm text-blue-700 space-y-1">
                <p>
                  • Click "Check Health" to verify your current authentication
                  status
                </p>
                <p>
                  • Click "Login to Weeztix" to authenticate via OAuth when
                  needed
                </p>
                <p>
                  • You'll be redirected to Weeztix, then back after successful
                  login
                </p>
                <p>• Tokens are stored securely and refreshed automatically</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default WeeztixAuth
